import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetPropertyByIdQuery } from 'src/redux/api'
import MyModal from 'src/components/ui/modal/MyModal'
import { translateParamValue } from 'src/components/elements/property-parameters/PropertyParameters'
import { IProperty } from 'src/interfaces/IProperty'
import { formatPrice } from 'src/helpers/formatPrice'
import { CheckMarkIcon } from '@svgs'

// a yes: drawn as a check mark, read as "так"
const YES = Symbol('yes')

// Merged row-by-row comparison (roadmap 29.3): one row per parameter across
// all picked listings, rows where values differ get highlighted — that is the
// entire point of comparing.

interface ICompareModal {
  open: boolean
  handleClose: () => void
  ids: string[]
}

const formatValue = (
  t: (key: string, options?: { defaultValue: string }) => string,
  key: string,
  value: unknown,
): string | typeof YES => {
  if (value === null || value === undefined || value === '' || value === false) return '—'
  if (value === true) return YES
  if (Array.isArray(value)) {
    if (value.length === 0) return '—'
    return value.map((item) => translateParamValue(t, key, String(item))).join(', ')
  }
  if (typeof value === 'number') return String(value)
  if (typeof value === 'object') return '—'
  return translateParamValue(t, key, String(value))
}

const CompareModal: FC<ICompareModal> = ({ open, handleClose, ids }) => {
  const { t } = useTranslation()

  // The compare set is capped at 3, so a fixed trio of hooks covers it.
  const first = useGetPropertyByIdQuery(open && ids[0] ? ids[0] : (skipToken as any))
  const second = useGetPropertyByIdQuery(open && ids[1] ? ids[1] : (skipToken as any))
  const third = useGetPropertyByIdQuery(open && ids[2] ? ids[2] : (skipToken as any))
  const properties = [first.data, second.data, third.data].filter(Boolean) as IProperty[]

  // Union of parameter keys that mean something for at least one listing.
  const paramKeys = Array.from(
    new Set(
      properties.flatMap((property) =>
        Object.entries(property.parametersSection || {})
          .filter(
            ([, value]) =>
              (value !== null &&
                value !== undefined &&
                value !== '' &&
                value !== false &&
                !(Array.isArray(value) && value.length === 0) &&
                typeof value !== 'object') ||
              Array.isArray(value),
          )
          .map(([key]) => key),
      ),
    ),
  )

  const rows: { label: string; values: (string | typeof YES)[] }[] = [
    {
      label: t('price'),
      values: properties.map((property) => {
        const amount = Array.isArray(property.price)
          ? property.price.find((entry) => entry.currency === property.currency)?.amount
          : undefined
        return amount ? formatPrice(amount, property.currency) : '—'
      }),
    },
    {
      label: t('step1.settlement'),
      values: properties.map(
        (property) =>
          `${property.addressSection?.settlement || ''}${
            property.addressSection?.street ? `, ${property.addressSection.street}` : ''
          }` || '—',
      ),
    },
    {
      label: t('views'),
      values: properties.map((property) =>
        typeof property.viewsCount === 'number' ? String(property.viewsCount) : '—',
      ),
    },
    ...paramKeys.map((key) => ({
      label: t(`step3.${key}`, { defaultValue: key }),
      values: properties.map((property) =>
        formatValue(
          t,
          key,
          (property.parametersSection as unknown as Record<string, unknown>)?.[key],
        ),
      ),
    })),
  ]

  return (
    <MyModal width="max-w-[70rem] w-full" open={open} handleClose={handleClose}>
      <div className="flex flex-col gap-4 p-4 max-h-[80vh] overflow-y-auto">
        <h3 className="text-xl font-black text-ink">{t('compare.title')}</h3>

        {/* Header: photos and links */}
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: `10rem repeat(${properties.length}, 1fr)` }}
        >
          <div />
          {properties.map((property) => (
            <Link key={property.id} to={`/announcement/${property.id}`} onClick={handleClose}>
              <img
                src={property.images?.[0]}
                alt=""
                className="w-full h-28 object-cover rounded-xl bg-brand-soft"
              />
            </Link>
          ))}
          <div />
        </div>

        {/* Merged parameter rows; differing values get highlighted */}
        <div className="flex flex-col text-sm">
          {rows.map((row) => {
            const meaningful = row.values.filter((value) => value !== '—')
            if (meaningful.length === 0) return null
            const differs = new Set(row.values).size > 1
            return (
              <div
                key={row.label}
                className={clsx(
                  'grid gap-2 px-2 py-2 rounded-lg items-center',
                  differs ? 'bg-warning-soft' : 'odd:bg-page',
                )}
                style={{ gridTemplateColumns: `10rem repeat(${properties.length}, 1fr)` }}
              >
                <div className="font-bold text-ink-2">{row.label}</div>
                {row.values.map((value, index) => (
                  <div key={index} className="text-ink">
                    {value === YES ? (
                      <>
                        <CheckMarkIcon className="w-4 h-4 text-success" />
                        <span className="sr-only">{t('compare.yes')}</span>
                      </>
                    ) : (
                      value
                    )}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </div>
    </MyModal>
  )
}

export default CompareModal
